import { useTranslation } from 'react-i18next';
import {
  Toggle,
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownMenuItem,
  SkeletonBase,
} from '@unified-checkout/design-system';
import { CircleInfo } from '@unified-checkout/design-system/icons/index';
import { Tooltip } from '@unified-checkout/design-system';
import { getPaymentMethod } from '@unified-checkout/design-system';
import styles from './settings.module.scss';

interface Integration {
  gateway_type: string;
  id: number;
  integration_name?: string;
}

interface PaymentMethodsListProps {
  selectedCurrency: string;
  isIntegrationsLoading: boolean;
  availablePaymentMethods: Map<string, Array<Integration>>;
  selectedIntegrationIds: Map<string, number>;
  paymentMethodsList: number[];
  isActive: boolean;
  isUpdatePending: boolean;
  isStatusChangePending: boolean;
  onPaymentMethodToggle: (gatewayType: string, enabled: boolean) => void;
  onIntegrationIdSelect: (gatewayType: string, integrationId: number) => void;
}

export const PaymentMethodsList = ({
  selectedCurrency,
  isIntegrationsLoading,
  availablePaymentMethods,
  selectedIntegrationIds,
  paymentMethodsList,
  isActive,
  isUpdatePending,
  isStatusChangePending,
  onPaymentMethodToggle,
  onIntegrationIdSelect,
}: PaymentMethodsListProps) => {
  const { t } = useTranslation();

  if (!selectedCurrency) {
    return null;
  }

  if (isIntegrationsLoading) {
    return (
      <div className={styles['settings-payment-methods']}>
        <SkeletonBase height="1.25rem" width="8rem" />
        <div className={styles['settings-payment-methods__list']}>
          <SkeletonBase height="3.5rem" width="100%" />
          <SkeletonBase height="3.5rem" width="100%" />
          <SkeletonBase height="3.5rem" width="100%" />
        </div>
      </div>
    );
  }

  if (availablePaymentMethods.size === 0) {
    return null;
  }

  return (
    <div className={styles['settings-payment-methods']}>
      <p className={styles['settings-payment-methods__label']}>
        {t(
          'innerLayout.paymePage.settings.paymentMethods.title',
          'Payment Methods'
        )}
      </p>
      <div className={styles['settings-payment-methods__list']}>
        {Array.from(availablePaymentMethods.entries())
          .sort((a, b) => {
            const [gatewayTypeA] = a;
            const [gatewayTypeB] = b;
            
            // Get payment method info to check names
            const paymentMethodA = getPaymentMethod(gatewayTypeA);
            const paymentMethodB = getPaymentMethod(gatewayTypeB);
            
            // Priority order for top 3
            const priorityOrder = ['Card', 'Mobile Wallet', 'Bank Installments'];
            const priorityA = priorityOrder.indexOf(paymentMethodA.name);
            const priorityB = priorityOrder.indexOf(paymentMethodB.name);
            
            // If both are in priority list, sort by priority order
            if (priorityA !== -1 && priorityB !== -1) {
              return priorityA - priorityB;
            }
            
            // If only A is in priority list, A comes first
            if (priorityA !== -1) {
              return -1;
            }
            
            // If only B is in priority list, B comes first
            if (priorityB !== -1) {
              return 1;
            }
            
            // Both are not in priority list, sort alphabetically
            return gatewayTypeA.localeCompare(gatewayTypeB);
          })
          .map(([gatewayType, integrations]) => {
            const selectedId =
              selectedIntegrationIds.get(gatewayType) ||
              integrations[0]?.id;
            const isEnabled = paymentMethodsList.includes(selectedId);
            const hasMultipleIntegrations = integrations.length > 1;
            const paymentMethodInfo = getPaymentMethod(gatewayType);

            return (
              <div
                key={gatewayType}
                className={styles['settings-payment-methods__item']}
              >
                <div
                  className={
                    styles['settings-payment-methods__item-row']
                  }
                >
                  <div className={styles['settings-payment-methods__name-wrapper']}>
                    <div className={styles['settings-payment-methods__icon']}>
                      {typeof paymentMethodInfo.iconRounded === 'string' ? (
                        <img
                          src={paymentMethodInfo.iconRounded}
                          alt={paymentMethodInfo.name}
                        />
                      ) : (
                        paymentMethodInfo.iconRounded
                      )}
                    </div>
                    <span
                      className={
                        styles['settings-payment-methods__name']
                      }
                    >
                      {paymentMethodInfo.name}
                    </span>
                  </div>
                  <Toggle
                    checked={isEnabled}
                    onChangeHandler={(checked: boolean) =>
                      onPaymentMethodToggle(gatewayType, checked)
                    }
                    disabled={
                      !isActive || isUpdatePending || isStatusChangePending
                    }
                  />
                </div>
                {isEnabled && hasMultipleIntegrations && (
                  <div
                    className={
                      styles[
                        'settings-payment-methods__integration-dropdown'
                      ]
                    }
                  >
                    <div className={styles['settings-divider']}></div>
                    <div
                      className={
                        styles[
                          'settings-payment-methods__integration-label'
                        ]
                      }
                    >
                      <span>
                        {t(
                          'innerLayout.paymePage.settings.paymentMethods.integrationId',
                          'Integration ID'
                        )}
                      </span>
                      <Tooltip
                        text={t(
                          'innerLayout.paymePage.settings.paymentMethods.integrationIdTooltip',
                          'An Integration ID uniquely identifies a payment method. You may have multiple Integration IDs for the same method.'
                        )}
                        triggerOn="hover"
                        centerArrow={true}
                      >
                        <div
                          className={
                            styles[
                              'settings-payment-methods__info-icon'
                            ]
                          }
                        >
                          {CircleInfo}
                        </div>
                      </Tooltip>
                    </div>
                    <Dropdown label="" width="full">
                      <DropdownButton
                        text={
                          integrations.find((i) => i.id === selectedId)
                            ?.integration_name || String(selectedId)
                        }
                        disabled={
                          !isActive || isUpdatePending || isStatusChangePending
                        }
                      />
                      <DropdownMenu verticalPlacement='auto'>
                        {integrations.map((integration) => (
                          <DropdownMenuItem
                            key={integration.id}
                            text={
                              integration.integration_name ||
                              String(integration.id)
                            }
                            selected={selectedId === integration.id}
                            onClickHandler={() =>
                              onIntegrationIdSelect(gatewayType, integration.id)
                            }
                          />
                        ))}
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
};
